/**
 * The canonical theme-color tokens — the ONLY values a `ColorValue.token` may hold. Each name is EXACTLY
 * the `--aqx-theme-<name>` var suffix (0019 §4a): `brand`/`line`/`background` were renamed to
 * `accent`/`border`/`bg`; `success`/`star`/`outline` are added. Saved docs carrying the old names are
 * remapped on load by `hydrateDocument` (0019 finding #5).
 */
export const THEME_COLOR_TOKENS = [
    'accent',
    // The seller's SECOND accent (theme settings "Accent 2"). Added because a document had no way to point at
    // it — every pale-accent surface in the default theme was therefore a FROZEN hex that ignored the seller's
    // palette. `themeColorVar` is the identity, so it resolves to the `--aqx-theme-accent-2` applyThemeVars
    // already emits; no new plumbing.
    'accent-2',
    'bg',
    'surface',
    'ink',
    'border',
    'success',
    'star',
    'outline',
];
/**
 * token → the CSS var it resolves to. An IDENTITY function `--aqx-theme-${token}` (not a hand-written
 * table) so it CANNOT drift from `settingValue.resolveColor`'s verbatim interpolation.
 */
export const themeColorVar = (t) => `--aqx-theme-${t}`;
/**
 * Derived record of token → var, kept for callers that index a map (e.g. the inspector swatch grid) and
 * the lockstep test. Cannot drift: it is computed from {@link themeColorVar}, never hand-maintained.
 */
export const THEME_COLOR_VAR = Object.fromEntries(THEME_COLOR_TOKENS.map((t) => [t, themeColorVar(t)]));
/** Narrowing guard: is `token` one of the canonical theme tokens? */
export function isThemeColorToken(token) {
    return THEME_COLOR_TOKENS.includes(token);
}
/**
 * The resolver the inspector ColorControl + the 8 blocks/sections importing from here use.
 *   { ref:'theme', token }  → `var(--aqx-theme-<token>)` (reflows on re-theme)
 *   { ref:'custom', value } → the literal value
 * An unknown/legacy token (e.g. the pre-0019 `brand`, or a hostile string) returns undefined so the
 * caller falls through to its own field default instead of interpolating an arbitrary string into a var
 * (the front-side allowlist, 0019 finding #18). Saved `brand`/`line`/`background`/`lavender` tokens are
 * remapped to the new names by `hydrateDocument` before they ever reach here.
 */
export function resolveColor(v) {
    if (!v)
        return undefined;
    if (v.ref === 'custom')
        return v.value;
    return isThemeColorToken(v.token) ? `var(--aqx-theme-${v.token})` : undefined;
}
//# sourceMappingURL=colorTokens.js.map